import { createStore } from 'zustand'
import { ReaderSummaryStore } from './types'
import {
  generateSummary,
  sendSummaryFeedback,
  updateStatsReaderSummaryOptIn,
} from '../../requests'
import { analytics } from 'segment_analytics'
import {
  canViewSummary,
  getSummaryVisibilityStatus,
  isStatsSelfView,
  isSummaryValid,
} from '../../utils'

const MIN_YEAR = 2025

export const createReaderSummaryStore = () => {
  return createStore<ReaderSummaryStore>()((set, get) => ({
    stats: null,
    isZeroState: false,
    status: 'hidden',
    summary: '',
    summaryId: '',
    summaryFeedback: 'unselected',
    summaryOptIn: false,
    canView: false,
    isSelfView: isStatsSelfView || false,
    sendFeedback: async ({ feedback, positive }) => {
      const { stats, summary, summaryId } = get()

      analytics.events.stats.statsSummaryBetaFeedback({
        positive,
        user_uuid: stats?.user?.id || '',
        summary,
        summary_id: summaryId,
      })

      try {
        await sendSummaryFeedback({
          userId: stats?.user?.id || '',
          statsId: stats?.id || '',
          data: {
            feedback_str: feedback,
            positive,
            reader_summary_id: summaryId,
          },
        })

        set({ summaryFeedback: positive ? 'positive' : 'negative' })
      } catch (e) {
        const err = e as Error
        console.error(
          `Summary feedback failed: ${err.message || 'Unknown error'}`
        )
      }
    },
    setSummaryData: ({
      summary,
      id,
      feedback = 'unselected',
      isZeroState = false,
    }) => {
      const { isSelfView, summaryOptIn, stats } = get()
      const canView = canViewSummary({ isSelfView, stats })

      set({
        summary: summary,
        summaryId: id,
        summaryFeedback: feedback,
        status: summaryOptIn ? 'success' : 'hidden',
        isZeroState,
        canView,
      })
    },
    updateSummaryOptIn: async (optIn) => {
      const { stats, setSummaryData, clearSummary } = get()
      const statsUserId = stats?.user?.id || ''

      if (!stats || !statsUserId) return

      const isInvalidYear = stats?.year < MIN_YEAR

      analytics.events.stats.statsSummaryBetaOptIn(optIn)

      set({ summaryOptIn: optIn, status: isInvalidYear ? 'hidden' : 'loading' })

      try {
        const { data } = await updateStatsReaderSummaryOptIn({
          userId: statsUserId,
          optIn,
        })

        clearSummary()

        if (isInvalidYear) return

        if (!!data && optIn) {
          setSummaryData({
            summary: data.summary,
            id: data.summary_id,
            isZeroState: data.zero_state,
          })
        } else {
          set({ status: 'hidden' })
        }
      } catch (e) {
        console.error('Error updating stats reader summary opt in', e)
        set({ status: 'hidden' })
      }
    },
    updateSummary: (stats) => {
      const { clearSummary, setSummaryData } = get()

      clearSummary()

      set({ stats })

      if (stats.year < MIN_YEAR) {
        set({ status: 'hidden' })
        return
      }

      setSummaryData({
        summary: stats.summary as string,
        id: stats.summary_id as string,
        feedback: stats.summary_feedback,
        isZeroState: !isSummaryValid(stats),
      })
    },
    clearSummary: () =>
      set({
        summary: '',
        summaryId: '',
        summaryFeedback: 'unselected',
        isZeroState: false,
      }),
    generateSummary: async () => {
      const { stats, setSummaryData, clearSummary } = get()
      const statsUserId = stats?.user?.id || ''

      if (!stats || !statsUserId) return

      const isInvalidYear = stats?.year < MIN_YEAR

      set({ status: isInvalidYear ? 'hidden' : 'loading' })
      clearSummary()

      if (isInvalidYear) return

      try {
        const { data } = await generateSummary({
          userId: statsUserId,
          statsId: stats.id,
        })

        if (!!data) {
          setSummaryData({
            summary: data.summary,
            id: data.summary_id,
            isZeroState: data.zero_state,
          })
        } else {
          set({ status: 'hidden' })
        }
      } catch (e) {
        console.error('Error generating stats reader summary opt in', e)
        set({ status: 'hidden' })
      }
    },
    setReaderSummary: ({ stats, isSelfView, isUserOptedIn }) => {
      const { clearSummary } = get()
      const summaryFeedback = stats?.summary_feedback || 'unselected'
      const summaryOptIn = !!stats?.summary_opt_in
      const { status, isZeroState, canView } = getSummaryVisibilityStatus({
        isSelfView,
        stats,
        isUserOptedIn,
      })

      clearSummary()

      set({
        stats,
        status,
        summary: stats?.summary || '',
        summaryId: stats?.summary_id || '',
        summaryFeedback,
        summaryOptIn,
        canView,
        isSelfView,
        isZeroState,
      })
    },
  }))
}
